import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert, Box, Button, Chip, Container, Stack, TextField, Typography,
} from '@mui/material';
import { useProject } from '../../hooks/ProjectContext';

function StatusChip({ status }) {
  const map = {
    pending: ['default', 'Pending'],
    visited: ['success', 'Visited'],
    skipped: ['warning', 'Skipped'],
    added:   ['info',    'Added'],
  };
  const [color, label] = map[status] || ['default', status];
  return <Chip label={label} color={color} size="small" />;
}

function GpsSignalChip({ accuracyM }) {
  if (accuracyM == null) return <Chip size="small" label="GPS…" sx={{ bgcolor: 'grey.300', color: 'grey.700' }} />;
  if (accuracyM <= 5)  return <Chip size="small" label={`±${Math.round(accuracyM)}m ✓`} sx={{ bgcolor: '#2e7d32', color: '#fff' }} />;
  if (accuracyM <= 10) return <Chip size="small" label={`±${Math.round(accuracyM)}m`} sx={{ bgcolor: '#388e3c', color: '#fff' }} />;
  if (accuracyM <= 20) return <Chip size="small" label={`±${Math.round(accuracyM)}m`} sx={{ bgcolor: '#f57c00', color: '#fff' }} />;
  return <Chip size="small" label={`±${Math.round(accuracyM)}m`} sx={{ bgcolor: '#c62828', color: '#fff' }} />;
}

export default function FieldRecordPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedProject, selectedSite } = useProject();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);
  const [expandedPoint, setExpandedPoint] = useState(null);
  const [editState, setEditState] = useState({});
  const [finalizing, setFinalizing] = useState(false);
  const [gpsStatus, setGpsStatus] = useState({});
  const [liveAccuracy, setLiveAccuracy] = useState(null);

  const loadRecord = useCallback(() => {
    setLoading(true);
    fetch(`/v1/field-records/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject('Not found'))
      .then(data => { setRecord(data); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [id]);

  useEffect(() => { loadRecord(); }, [loadRecord]);

  // Watch live GPS accuracy
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      pos => setLiveAccuracy(pos.coords.accuracy ?? null),
      () => setLiveAccuracy(null),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 3000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  async function updatePoint(pointId, updates) {
    setSaving(pointId);
    const tkn = localStorage.getItem('tkn');
    await fetch(`/v1/field-records/${id}/points/${pointId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
      body: JSON.stringify(updates),
    });
    setSaving(null);
    loadRecord();
  }

  function captureGps(pointId) {
    setGpsStatus(prev => ({ ...prev, [pointId]: 'acquiring...' }));
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        setEditState(prev => ({
          ...prev,
          [pointId]: { ...(prev[pointId] || {}), actualLat: lat, actualLng: lng },
        }));
        setGpsStatus(prev => ({ ...prev, [pointId]: `${lat}, ${lng}` }));
      },
      () => setGpsStatus(prev => ({ ...prev, [pointId]: 'GPS unavailable' })),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }

  async function finalize() {
    if (!window.confirm('Finalize this field record? It cannot be edited after finalization.')) return;
    setFinalizing(true);
    const tkn = localStorage.getItem('tkn');
    await fetch(`/v1/field-records/${id}/finalize`, {
      method: 'POST',
      headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
    });
    setFinalizing(false);
    loadRecord();
  }

  function setEdit(pointId, key, value) {
    setEditState(prev => ({ ...prev, [pointId]: { ...(prev[pointId] || {}), [key]: value } }));
  }

  function sampleIdsDisplay(p) {
    const es = editState[p.id] || {};
    if (es.sampleIds !== undefined) return es.sampleIds;
    try {
      const arr = typeof p.sample_ids === 'string' ? JSON.parse(p.sample_ids) : (p.sample_ids || []);
      return Array.isArray(arr) ? arr.join(', ') : '';
    } catch { return ''; }
  }

  if (loading) return <Container><Typography sx={{ py: 4 }}>Loading...</Typography></Container>;
  if (error || !record) return <Container><Alert severity="error" sx={{ mt: 2 }}>{error || 'Record not found'}</Alert></Container>;

  const points = record.points || [];
  const done = points.filter(p => ['visited', 'skipped', 'added'].includes(p.status)).length;
  const isFinalized = record.status === 'finalized';

  return (
    <>
      <Helmet><title>Field Record | TERRRA</title></Helmet>
      <Container maxWidth="md" sx={{ py: 2, pb: 8 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5">{record.campaign_name || 'Field Record'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {record.date} &middot; {done}/{points.length} points done
              {record.site_id ? ` · ${record.site_id}` : ''}
            </Typography>
            {record.notes && (
              <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>{record.notes}</Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <GpsSignalChip accuracyM={liveAccuracy} />
            <Chip
              label={isFinalized ? 'Finalized' : 'Draft'}
              color={isFinalized ? 'success' : 'default'}
              size="small"
            />
            <Button
              variant="outlined" size="small"
              onClick={() => {
                const params = new URLSearchParams({
                  fieldRecordId: id,
                  mode: 'sampling',
                  ...(selectedProject?.code ? { projectId: selectedProject.code } : {}),
                  ...(selectedSite?.code ? { siteId: selectedSite.code } : {}),
                });
                navigate(`/project/capture?${params}`);
              }}
            >
              Capture Photo
            </Button>
            {!isFinalized && (
              <Button variant="contained" size="small" color="success" onClick={finalize} disabled={finalizing}>
                {finalizing ? 'Finalizing…' : 'Finalize'}
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Progress bar */}
        {points.length > 0 && (
          <Box sx={{ mb: 2, height: 6, borderRadius: 3, bgcolor: 'grey.200', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.round((done / points.length) * 100)}%`, bgcolor: 'success.main', transition: 'width 0.3s' }} />
          </Box>
        )}

        {/* Points list */}
        <Stack spacing={1}>
          {points.map(p => {
            const isExpanded = expandedPoint === p.id;
            const es = editState[p.id] || {};
            const borderColor = p.status === 'visited' ? 'success.main' : p.status === 'skipped' ? 'warning.main' : p.status === 'added' ? 'info.main' : 'divider';

            return (
              <Box
                key={p.id}
                sx={{ border: '1px solid', borderColor, borderRadius: 1, p: 1.5 }}
              >
                {/* Row summary */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold">{p.planned_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.planned_lat && p.planned_lng
                        ? `${parseFloat(p.planned_lat).toFixed(5)}, ${parseFloat(p.planned_lng).toFixed(5)}`
                        : 'No planned GPS'}
                      {p.evidence_count > 0 && <> &middot; {p.evidence_count} photo{p.evidence_count > 1 ? 's' : ''}</>}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1 }}>
                    <StatusChip status={p.status} />
                    {!isFinalized && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setExpandedPoint(isExpanded ? null : p.id)}
                      >
                        {isExpanded ? 'Close' : 'Edit'}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                {/* Expanded edit form */}
                {isExpanded && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    {/* Status buttons */}
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                      <Button
                        size="small"
                        variant={p.status === 'visited' ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => updatePoint(p.id, {
                          status: 'visited',
                          ...(es.actualLat ? { actual_lat: es.actualLat, actual_lng: es.actualLng } : {}),
                        })}
                      >
                        Visited
                      </Button>
                      <Button
                        size="small"
                        variant={p.status === 'skipped' ? 'contained' : 'outlined'}
                        color="warning"
                        onClick={() => updatePoint(p.id, {
                          status: 'skipped',
                          skip_reason: es.skipReason || p.skip_reason || null,
                        })}
                      >
                        Skipped
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => captureGps(p.id)}>
                        Capture GPS
                      </Button>
                      <GpsSignalChip accuracyM={liveAccuracy} />
                    </Stack>

                    {/* GPS feedback */}
                    {gpsStatus[p.id] && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
                        GPS: {gpsStatus[p.id]}
                      </Typography>
                    )}
                    {!gpsStatus[p.id] && p.actual_lat && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
                        Actual GPS: {p.actual_lat}, {p.actual_lng}
                      </Typography>
                    )}

                    {/* Sample IDs */}
                    <TextField
                      size="small" fullWidth label="Sample IDs (comma separated)"
                      value={sampleIdsDisplay(p)}
                      onChange={e => setEdit(p.id, 'sampleIds', e.target.value)}
                      placeholder="e.g. BUR-001-A, BUR-001-B"
                      sx={{ mb: 1 }}
                    />

                    {/* Skip reason */}
                    {(p.status === 'skipped' || es.markSkipped) && (
                      <TextField
                        size="small" fullWidth label="Skip reason"
                        value={es.skipReason !== undefined ? es.skipReason : (p.skip_reason || '')}
                        onChange={e => setEdit(p.id, 'skipReason', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                    )}

                    {/* Notes */}
                    <TextField
                      size="small" fullWidth label="Notes" multiline rows={2}
                      value={es.notes !== undefined ? es.notes : (p.notes || '')}
                      onChange={e => setEdit(p.id, 'notes', e.target.value)}
                      sx={{ mb: 1 }}
                    />

                    {/* Save + Capture Photo */}
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small" variant="contained"
                        disabled={saving === p.id}
                        onClick={() => {
                          const updates = {};
                          if (es.actualLat) { updates.actual_lat = es.actualLat; updates.actual_lng = es.actualLng; }
                          if (es.sampleIds !== undefined) {
                            updates.sample_ids = es.sampleIds.split(',').map(s => s.trim()).filter(Boolean);
                          }
                          if (es.notes !== undefined) updates.notes = es.notes;
                          if (es.skipReason !== undefined) updates.skip_reason = es.skipReason;
                          updatePoint(p.id, updates);
                          setExpandedPoint(null);
                        }}
                      >
                        {saving === p.id ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="small" variant="outlined"
                        onClick={() => {
                          const params = new URLSearchParams({
                            fieldRecordId: id,
                            mode: 'sampling',
                            ...(selectedProject?.code ? { projectId: selectedProject.code } : {}),
                            ...(selectedSite?.code ? { siteId: selectedSite.code } : {}),
                          });
                          navigate(`/project/capture?${params}`);
                        }}
                      >
                        Capture Photo
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>

        {points.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No planned points. This record was created without a linked campaign.
          </Alert>
        )}
      </Container>
    </>
  );
}
